import cn from 'classnames'
import type { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import type { TReplaceOptions } from 'cng-common/features/translations/components/translation/models/replaceOptions'
import { Button } from 'core-components/library/button'
import { TPopupHorizontal } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import {
  isAttentionSeverityTop,
  type TAttentionStatusSeverity,
} from 'partner360/entities/attentionStatuses'

import styles from './AttentionNextSteps.module.scss'

/** What the step's button does: goes to the place that answers it, signs, or reminds a signer. */
export type TAttentionNextStepTone = 'go' | 'sign' | 'remind'

/** The step's mark: a section in its own colour, a category, a person — the reader in blue. */
export interface IAttentionNextStepMark {
  kind: 'section' | 'category' | 'person' | 'plan'
  text: string
  color?: string
  isOwn?: boolean
}

/** A second act before the step's own button, e.g. opening the plan. */
export interface IAttentionNextStepExtraAction {
  icon: string
  labelKey: string
  /** A short word said on the button before its icon; without one the button is its icon alone. */
  shortLabelKey?: string
  onClick: () => void
}

interface IAttentionNextStepRowProps {
  mark: IAttentionNextStepMark
  name: ReactNode
  /** The figure the step is read by, e.g. `20% complete` or a signer's role. */
  fact?: ReactNode
  /** Said after the name as the reader's own row, e.g. `You`. */
  badge?: ReactNode
  tone: TAttentionNextStepTone
  /** The status' level: a step that goes somewhere wears its colour. */
  severity: TAttentionStatusSeverity
  /** The button's name, said in its tooltip; a step the reader cannot act on has none and shows
   *  no button. */
  labelKey?: string
  labelParams?: TReplaceOptions[]
  /** A short word said on the button before its icon, e.g. `Plan`; the tooltip keeps the full
   *  name. Without one the button is its icon alone. */
  shortLabelKey?: string
  /** The button's icon where the step's tone does not name the place it goes to. */
  icon?: string
  extraAction?: IAttentionNextStepExtraAction
  /** The fact on a line of its own under the name, for a figure that may run to a sentence. */
  stacked?: boolean
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

const TONE_ICON: Record<TAttentionNextStepTone, string> = {
  go: 'kbase kbase-arrow-right',
  sign: 'kbase kbase-check',
  remind: 'kbase kbase-bell',
}

/** The button's icon alone, or a short word with the icon closing it. */
const buttonFace = (icon: string, shortLabelKey?: string) =>
  shortLabelKey
    ? {
        icon,
        iconPosition: 'end' as const,
        children: <Translation span text={shortLabelKey} />,
      }
    : { iconButton: icon }

/**
 * One next step under a status: who or what it is about, the figure it is read by and the button
 * that does it. The button is its icon alone — an arrow to go, a check to sign, a bell to remind —
 * and names itself in the tooltip, so the steps stand as one narrow column of equal squares. Given a
 * short word, the button says it before the icon, as the manager cockpit's steps do.
 */
export const AttentionNextStepRow = ({
  mark,
  name,
  fact,
  badge,
  tone,
  severity,
  labelKey,
  labelParams,
  shortLabelKey,
  icon,
  extraAction,
  stacked = false,
  onClick,
  disabled = false,
  loading = false,
}: IAttentionNextStepRowProps) => {
  /* Signing keeps the success colour the plan's signature wears everywhere; every other step
     takes its status' level, so a reminder reads as urgent as the status it answers. */
  const color =
    tone === 'sign' ? 'success' : isAttentionSeverityTop(severity) ? 'danger' : 'warning'

  return (
    /* The row under the pointer takes the tint of its own button, so the two never disagree. */
    <div className={cn(styles.row, styles[`row_${color}`], { [styles.rowStacked]: stacked })}>
      <span
        className={cn(styles.mark, styles[`mark_${mark.kind}`], { [styles.markOwn]: mark.isOwn })}
        style={mark.color ? { background: mark.color } : undefined}
        aria-hidden="true"
      >
        {mark.kind === 'plan' ? <i className="kbase kbase-check" /> : mark.text}
      </span>

      <span className={cn(styles.rowText, { [styles.rowTextStacked]: stacked })}>
        <span className={styles.rowName}>
          {name}
          {badge}
        </span>
        {fact && <span className={styles.rowFact}>{fact}</span>}
      </span>

      {extraAction && (
        <Tooltip
          className={styles.rowActionTip}
          horizontalDirection={TPopupHorizontal.Left}
          horizontalStartPosition={TPopupHorizontal.Right}
          tipContent={() => <Translation span text={extraAction.labelKey} />}
        >
          <Button
            className={cn(styles.rowAction, styles.rowAction_primary, {
              [styles.rowActionWord]: extraAction.shortLabelKey,
            })}
            variant="custom"
            size="sm"
            {...buttonFace(extraAction.icon, extraAction.shortLabelKey)}
            onClick={extraAction.onClick}
          />
        </Tooltip>
      )}

      {labelKey && (
        <Tooltip
          className={styles.rowActionTip}
          horizontalDirection={TPopupHorizontal.Left}
          horizontalStartPosition={TPopupHorizontal.Right}
          tipContent={() => <Translation span text={labelKey} replaceParams={labelParams} />}
        >
          <Button
            className={cn(styles.rowAction, styles[`rowAction_${color}`], {
              [styles.rowActionWord]: shortLabelKey,
            })}
            variant="custom"
            size="sm"
            {...buttonFace(icon ?? TONE_ICON[tone], shortLabelKey)}
            disabled={disabled}
            loading={loading}
            onClick={onClick}
          />
        </Tooltip>
      )}
    </div>
  )
}

AttentionNextStepRow.displayName = 'AttentionNextStepRow'
